/**
 * Setup-wizard rate rules shared by web, mobile and the backend checklist.
 *
 * A crew/truck rate at $0 prices labor at nothing, so the wizard refuses to
 * mark the rates step done until every rate the formula will use is above $0.
 */

export type RequiredRateSlug = 'supervisor' | 'mover' | 'driver' | 'truck' | 'truck_driver';

export const RATE_LABELS: Record<RequiredRateSlug, string> = {
  supervisor: 'Supervisor',
  mover: 'Mover',
  driver: 'Driver',
  truck: 'Truck',
  truck_driver: 'Truck + Driver',
};

/** One-line explanations shown under each rate in the setup wizard. */
export const RATE_EXPLANATIONS: Record<RequiredRateSlug, string> = {
  supervisor: 'The crew lead on every job. Billed per hour for every hour on the clock.',
  mover: 'Each additional crew member. The formula picks how many movers a job needs.',
  driver: 'The person driving each truck, billed separately from the truck itself.',
  truck: 'Each truck on the job, billed per hour on top of its driver.',
  truck_driver: 'One combined hourly rate for a truck and its driver, billed per truck.',
};

export const BLENDED_RATE_EXPLANATION =
  'On: one "Truck + Driver" rate covers both. Off: the truck and its driver are billed as two separate rates.';

/** The rates a quote actually uses, depending on how trucks and drivers are billed. */
export function requiredRateSlugs(useBlendedTruckDriverRate: boolean): RequiredRateSlug[] {
  return useBlendedTruckDriverRate
    ? ['supervisor', 'mover', 'truck_driver']
    : ['supervisor', 'mover', 'driver', 'truck'];
}

/**
 * Required rates that are missing or $0, in display order. Empty when the
 * rates step can be marked complete.
 */
export function missingRequiredRates(
  rates: Partial<Record<string, number | null | undefined>>,
  useBlendedTruckDriverRate: boolean,
): RequiredRateSlug[] {
  return requiredRateSlugs(useBlendedTruckDriverRate).filter(slug => {
    const value = rates[slug];
    return typeof value !== 'number' || !Number.isFinite(value) || value <= 0;
  });
}

/** Message for the wizard's blocking banner, or null when nothing is missing. */
export function missingRatesMessage(missing: RequiredRateSlug[]): string | null {
  if (missing.length === 0) return null;
  const names = missing.map(slug => RATE_LABELS[slug]);
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Enter an hourly rate above $0 for ${list} before continuing. Quotes priced with a $0 rate come out at $0.`;
}

/** A residential flat-rate tier as the wizard validates it. */
export interface FlatRateTierInput {
  enabled: boolean;
  flatRate: number;
  label?: string;
  bedroomCount?: number;
}

/** Enabled bedroom tiers priced at $0 (or no enabled tier at all → message). */
export function flatRateTiersMessage(rooms: FlatRateTierInput[]): string | null {
  const enabled = rooms.filter(r => r.enabled);
  if (enabled.length === 0) {
    return 'Enable at least one bedroom tier with a price above $0 before continuing.';
  }
  const unpriced = enabled.filter(r => !Number.isFinite(r.flatRate) || r.flatRate <= 0);
  if (unpriced.length === 0) return null;
  const names = unpriced.map(r => r.label || `${r.bedroomCount ?? '?'} BR`);
  return `Enter a flat rate above $0 for ${names.join(', ')} before continuing. A $0 tier produces a $0 quote.`;
}
